import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import DatePicker from './DatePicker';
import { Upload, RefreshCw } from 'lucide-react';
import { uploadLicenseFile } from '../services/api';
import { toast } from 'sonner';

const LicenseUpdateDialog = ({ open, onOpenChange, license, onSuccess }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [indet, setIndet] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && license) {
      setFile(null);
      setIssueDate(license.issue_date ? license.issue_date.split('T')[0] : '');
      setExpiryDate(license.expiry_date ? license.expiry_date.split('T')[0] : '');
      setIndet(!!license.indeterminate_expiry);
      if (inputRef.current) inputRef.current.value = '';
    }
  }, [open, license]);

  const handleSubmit = async () => {
    if (!file) { toast.error('Selecione o novo arquivo do alvará'); return; }
    setSaving(true);
    try {
      const opts = { kind: 'arquivo', indeterminate_expiry: indet };
      if (issueDate) opts.issue_date = issueDate;
      if (!indet && expiryDate) opts.expiry_date = expiryDate;
      await uploadLicenseFile(license.id, file, opts);
      toast.success('Licença atualizada com sucesso');
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar a licença');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-gray-200 max-w-lg" data-testid="license-update-dialog">
        <DialogHeader>
          <DialogTitle className="text-gray-900 flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-[#003366]" /> Atualizar Licença
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            {license?.name ? `Envie a nova versão do arquivo de "${license.name}" e atualize as datas do alvará.` : 'Envie a nova versão e atualize as datas.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-1">
            <Label className="text-gray-700 font-semibold">Novo arquivo *</Label>
            <input
              ref={inputRef}
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              className="hidden"
              data-testid="update-file-input"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} className="bg-white text-gray-700 border-gray-300 text-sm" data-testid="update-pick-file">
                <Upload className="w-4 h-4 mr-2" /> Escolher arquivo
              </Button>
              {file && <span className="text-sm text-gray-600 truncate max-w-[240px]">{file.name}</span>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-gray-700">Data de Emissão</Label>
              <DatePicker value={issueDate} onChange={setIssueDate} placeholder="Emissão" data-testid="update-issue-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-gray-700">Data de Vencimento</Label>
              <DatePicker value={expiryDate} onChange={setExpiryDate} placeholder="Vencimento" disabled={indet} data-testid="update-expiry-date" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <Checkbox checked={indet} onCheckedChange={(v) => setIndet(!!v)} data-testid="update-indeterminate" />
            Prazo indeterminado (sem vencimento)
          </label>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="bg-white text-gray-700 border-gray-300" data-testid="update-cancel-btn">
            Cancelar
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={saving || !file} className="bg-[#003366] hover:bg-[#002244] text-white" data-testid="update-submit-btn">
            {saving ? 'Atualizando...' : 'Atualizar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LicenseUpdateDialog;
